import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Article } from '../types/article';
import { getArticles, getCategories } from '../api/articles';
import ArticleList from '../components/ArticleList';
import DateFilter from '../components/DateFilter';
import CategoryFilter from '../components/CategoryFilter';
import ManualInputForm from '../components/ManualInputForm';
import '../styles/EditPage.css';

const CONFIRMED_DATES_KEY = 'space-radar-confirmed-dates';
const MAX_CONFIRMED_DATES = 100;

const getConfirmedDatesFromStorage = (): string[] => {
  try {
    const stored = localStorage.getItem(CONFIRMED_DATES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

const saveConfirmedDatesToStorage = (dates: string[]) => {
  localStorage.setItem(CONFIRMED_DATES_KEY, JSON.stringify(dates));
};

const EditPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [showManualInput, setShowManualInput] = useState(false);
  const [confirmedDates, setConfirmedDates] = useState<string[]>(getConfirmedDatesFromStorage);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Load categories on mount
  useEffect(() => {
    const loadCats = async () => {
      try {
        const cats = await getCategories();
        const catsWithEtc = cats.includes('etc') ? cats : [...cats, 'etc'];
        setCategories(catsWithEtc);
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };
    loadCats();
  }, []);

  // Initialize date from URL or use today
  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      setSelectedDate(dateParam);
    } else {
      setSelectedDate(getTodayDate());
    }
  }, [searchParams]);

  // Load articles when date changes
  const loadArticles = useCallback(async () => {
    if (!selectedDate) return;

    setIsLoading(true);
    try {
      const data = await getArticles(selectedDate);
      setArticles(data);
    } catch (error) {
      console.error('Failed to load articles:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (selectedDate) {
      loadArticles();
    }
  }, [selectedDate, loadArticles]);

  // Update URL when date changes
  const handleApplyDateFilter = (date?: string) => {
    const targetDate = date ?? selectedDate;
    if (targetDate) {
      setSearchParams({ date: targetDate });
      setSelectedCategory(''); // Reset category filter
    }
  };

  // Handle date change from calendar
  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    handleApplyDateFilter(date);
  };

  // Filter articles by category
  const getFilteredArticles = () => {
    if (!selectedCategory) {
      return articles;
    }
    if (selectedCategory === 'etc') {
      const originalCategories = categories.filter((c) => c !== 'etc');
      return articles.filter((a) => !originalCategories.includes(a.category));
    }
    return articles.filter((a) => a.category === selectedCategory);
  };

  // Handle article update
  const handleArticleUpdate = (updatedArticle: Article) => {
    setArticles((prev) =>
      prev.map((a) => (a.id === updatedArticle.id ? updatedArticle : a))
    );
  };

  // Handle article delete
  const handleArticleDelete = (id: number) => {
    setArticles((prev) => prev.filter((a) => a.id !== id));
  };

  // Handle date confirmation
  const handleConfirmDate = () => {
    if (!selectedDate || confirmedDates.includes(selectedDate)) return;
    const newConfirmedDates =
      confirmedDates.length >= MAX_CONFIRMED_DATES
        ? [...confirmedDates.slice(1), selectedDate]
        : [...confirmedDates, selectedDate];
    setConfirmedDates(newConfirmedDates);
    saveConfirmedDatesToStorage(newConfirmedDates);
  };

  // Handle date unconfirmation
  const handleUnconfirmDate = () => {
    if (!selectedDate) return;
    const newConfirmedDates = confirmedDates.filter((d) => d !== selectedDate);
    setConfirmedDates(newConfirmedDates);
    saveConfirmedDatesToStorage(newConfirmedDates);
  };

  const isDateConfirmed = confirmedDates.includes(selectedDate);

  // Download articles as TXT
  const handleDownloadTxt = () => {
    if (!articles || articles.length === 0) {
      alert('다운로드할 기사가 없습니다.');
      return;
    }

    // Group by category
    const grouped: Record<string, Article[]> = {};
    articles.forEach((a) => {
      const cat = a.category || '미분류';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(a);
    });

    // Sort categories based on categoryOptions order
    const sortedKeys = [
      ...categories.filter((k) => grouped[k]),
      ...Object.keys(grouped).filter((k) => !categories.includes(k)),
    ];

    const textContent = sortedKeys
      .map((cat) => {
        const groupArticles = grouped[cat];
        const groupContent = groupArticles
          .map((a) => `${a.title || '제목 없음'} / ${a.url || 'URL 없음'}`)
          .join('\n\n');
        return `${cat}\n\n${groupContent}`;
      })
      .join('\n\n\n\n');

    const blob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);

    const dateStr = selectedDate || new Date().toISOString().split('T')[0];
    a.download = `articles_${dateStr}.txt`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const filteredArticles = getFilteredArticles();

  return (
    <div className="edit-container">
      <div className="panel">
        <div className="panel-header">📋 전체 기사 목록</div>

        <div className="date-filter">
          <DateFilter
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            onApply={() => handleApplyDateFilter()}
            confirmedDates={confirmedDates}
          />

          <div style={{ width: 20 }} />

          <CategoryFilter
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={(cat) => {
              setSelectedCategory(cat);
              // Scroll to top when category changes
              const panelContent = document.querySelector('.panel-content');
              if (panelContent) {
                panelContent.scrollTop = 0;
              }
            }}
          />

          <button className="btn-filter btn-download" onClick={handleDownloadTxt}>
            TXT 다운로드
          </button>
          <button
            className="btn-filter btn-manual"
            onClick={() => setShowManualInput(!showManualInput)}
          >
            직접입력
          </button>
        </div>

        {showManualInput && (
          <ManualInputForm
            categories={categories.filter((c) => c !== 'etc')}
            selectedDate={selectedDate}
            onArticleCreated={loadArticles}
            onClose={() => setShowManualInput(false)}
          />
        )}

        <div className="panel-content">
          {isLoading ? (
            <div className="loading">
              <div className="spinner"></div>
              <div>기사를 불러오는 중...</div>
            </div>
          ) : (
            <ArticleList
              articles={filteredArticles}
              categories={categories}
              selectedCategory={selectedCategory}
              onArticlesChange={setArticles}
              onArticleUpdate={handleArticleUpdate}
              onArticleDelete={handleArticleDelete}
              selectedDate={selectedDate}
              isDateConfirmed={isDateConfirmed}
              onConfirmDate={handleConfirmDate}
              onUnconfirmDate={handleUnconfirmDate}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EditPage;
